
import { Component, Vue } from 'vue-property-decorator';
import CalendarComponent, {
  CalendarEventInterface,
} from '@/views/pages/calendar/leaves/components/CalendarComponent.vue';
import {
  LeaveRequestInterface,
  LeaveRequestUpdateInterface,
  LeavesRepositoryInterface,
} from '@/services/Calendar/leaves/interfaces';
import { BaseProfileDataInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import FileRepositoryInterface from "@/services/File-manager/interfaces/file.interface";
import { FileEntity, FolderEntity } from '@fenix/file-manager-entities/file-manager-entities/entitites';
import DepartmentRepositoryInterface, { DepartmentInterface } from '@/services/Departments/Interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  components: {
    CalendarComponent,
    PageHeader,
  },
  inject: [
    'leavesRepository',
    'profileRepository',
    'fileRepository',
    'departmentRepository',
    'userRepository',
  ],
})
export default class AllLeaves extends Vue {
  private leavesRepository!: LeavesRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private fileRepository!: FileRepositoryInterface;
  private departmentRepository!: DepartmentRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private loading: boolean = true;
  private events: CalendarEventInterface[] = [];
  private leaves: LeaveRequestInterface[] = [];
  private pendingLeaves: LeaveRequestInterface[] = [];
  private colors: string[] = ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1'];
  private usersMap: { [userId: string]: BaseProfileDataInterface } = {};
  private departments: DepartmentInterface[] = [];
  private selectedDepartment: string = '';
  private selectedMonth: string = new Date().toISOString().substring(0, 7);

  private updateDialogVisible: boolean = false;
  private updateRequestId: number | null = null;
  private updateDateFrom: string | null = null;
  private updateDateTo: string | null = null;
  private updateDays: number | null = null;
  private updateRequestOn: string | null = null;

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    await this.aggregateLeaves();
    await this.aggregatePendingLeaves();
    await this.getDepartments();
    this.loading = false;
  }

  private async fetchMonthLeaves(date: Date): Promise<void> {
    if (new Date(this.selectedMonth).getMonth() === new Date(date).getMonth()) {
      return;
    }

    this.selectedMonth = new Date(date).toISOString().substring(0, 7);
    await this.aggregateLeaves();
  }

  private async getDepartments(): Promise<void> {
    this.departments = await this.departmentRepository.getAllDepartments();
  }

  private async getDepartmentLeaves(): Promise<void> {
    await this.aggregateLeaves();
  }

  private async aggregatePendingLeaves(): Promise<void> {
    this.pendingLeaves = [];
    this.pendingLeaves = await this.leavesRepository.getPendingRequests();
    const usersIds: string[] = [];
    this.pendingLeaves.forEach((pendingLeave) => {
      if (usersIds.indexOf(pendingLeave.requestBy) === -1) {
        usersIds.push(pendingLeave.requestBy);
      }
      if (usersIds.indexOf(pendingLeave.firstApprover) === -1) {
        usersIds.push(pendingLeave.firstApprover);
      }
      if (usersIds.indexOf(pendingLeave.secondApprover) === -1) {
        usersIds.push(pendingLeave.secondApprover);
      }
    });

    const users = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
    users.forEach((user) => {
      this.usersMap[user.userId] = user;
    });
  }

  private async aggregateLeaves(): Promise<void> {
    this.leaves = [];
    this.events = [];

    if (!this.selectedDepartment) {
      this.leaves = await this.leavesRepository.getAllLeaves(this.selectedMonth);
    } else {
      const departmentUsers = await this.userRepository.getUsersByDepartment(this.selectedDepartment);
      this.leaves = await this.leavesRepository.getByUsersIds(
        departmentUsers.map((user) => String(user.id)),
        this.selectedMonth,
      );
    }

    const users = await this.profileRepository
      .getBaseProfileDataByUsersIds(this.leaves.map((leave) => leave.requestBy));
    const usersColors: { [key: string]: string } = {};
    users.forEach((user) => {
      usersColors[user.userId] = this.colors[Math.floor(Math.random() * this.colors.length)];
    });

    this.leaves.forEach((leave) => {
      const requesterIndex = users.findIndex((user) => user.userId === leave.requestBy);
      if (requesterIndex === -1) {
        return;
      }

      const reviewerIndex = users.findIndex((user) => user.userId === leave.reviewedBy);

      this.events.push({
        userId: users[requesterIndex].userId,
        name: `${users[requesterIndex].firstName} ${users[requesterIndex].lastName} - ${leave.leaveType.name}`,
        start: new Date(leave.dateFrom),
        end: new Date(leave.dateTo),
        color: usersColors[leave.requestBy],
        details: `
          ${Vue.prototype.convertTimestamp(leave.dateFrom, true)}
          - ${Vue.prototype.convertTimestamp(leave.dateTo, true)}
          <br/>
          <br/> Requested On: ${leave.requestOn}
          <br/> Days on leave: ${leave.days}
          <br/>
          <br/> Approved by: ${users[reviewerIndex]?.firstName} ${users[reviewerIndex]?.lastName}
        `,
        timed: false,
      });
    });
  }

  private async reviewRequest(request: LeaveRequestInterface, isApproved: boolean): Promise<void> {
    try {
      await this.leavesRepository.reviewRequest(request.id, isApproved);
      this.$successDialog.show('Request reviewed successfully!');
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.message);
    }
  }

  private openUpdateDialog(request: LeaveRequestInterface): void {
    this.updateRequestId = request.id;
    this.updateDateFrom = this.toYMD(request.dateFrom);
    this.updateDateTo = this.toYMD(request.dateTo);
    this.updateDays = request.days;
    this.updateRequestOn = request.requestOn;
    this.updateDialogVisible = true;
  }

  private async submitUpdateRequest(): Promise<void> {
    if (!this.updateRequestId) {
      return;
    }

    try {
      const payload: LeaveRequestUpdateInterface = {};

      const fromYmd = this.updateDateFrom || this.toYMD(this.updateDateFrom);
      const toYmd = this.updateDateTo || this.toYMD(this.updateDateTo);

      if (this.updateDateFrom) {
        payload.dateFrom = new Date(this.updateDateFrom);
      }

      if (this.updateDateTo) {
        payload.dateTo = new Date(this.updateDateTo);
      }

      if(this.updateRequestOn) {
        payload.requestOn = new Date(this.updateRequestOn);
      }

      const selectedDaysArray = this.getDatesArray(fromYmd, toYmd);
      payload.selectedDays = JSON.stringify(selectedDaysArray);

      if (this.updateDays != null) {
        payload.days = this.updateDays;
      }

      await this.leavesRepository.updateLeaveRequest(this.updateRequestId, payload);

      this.$successDialog.show('Request updated successfully!');
      this.updateDialogVisible = false;
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.message || 'Could not update request');
    }
  }



  private async reviewCancellation(request: LeaveRequestInterface, isApproved: boolean): Promise<void> {
    try {
      await this.leavesRepository.reviewCancellationRequest(request.id, isApproved);
      this.$successDialog.show('Request reviewed successfully!');
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.message);
    }
  }

  private async downloadDocument(request: LeaveRequestInterface): Promise<void> {
    const file = new FileEntity();
    const folder = new FolderEntity();
    folder.private = true;
    file.name = this.generateLeaveDocumentName(request);
    file.folder = folder;

    if (request.file != null) {
      file.path = request.file;
    }

    await this.fileRepository.downloadFile(file);
  }

  private generateLeaveDocumentName(request: LeaveRequestInterface): string {
    const serialNumber = '0'.repeat(6 - String(request.id).length) + request.id;
    const name = this.usersMap[request.requestBy].fullName
      ? this.usersMap[request.requestBy].fullName
      : `${this.usersMap[request.requestBy]?.firstName} ${this.usersMap[request.requestBy].lastName}`;

    return `${request.leaveType.name[0]}${serialNumber} - ${name} - ${request.dateFrom}`;
  }

  private toYMD(d: string | Date) {
    return new Date(d).toISOString().slice(0, 10);
  }

  private getDatesArray(firstDate: string, lastDate: string): string[] {
    const arr: Date[] = [];
    for (const dt = new Date(firstDate); dt <= new Date(lastDate); dt.setDate(dt.getDate() + 1)) {
      arr.push(new Date(dt));
    }

    return arr.map((date) => date.toISOString()
      .replace(',', '')
      .substr(0, 10));
  }
}
